import { useTranslation } from 'react-i18next';
import { Select, SelectProps } from 'antd';
import { JSX, useMemo, ReactNode } from 'react';
import { IconSvg } from 'shared/icon';
import { StyledIconDefault } from 'shared/icon/Icon.styled';
import AdminSingleSelect from './AdminSingleSelect';
import AdminMultiSelect from './AdminMultiSelect';
import AdminOrderedMultiSelect from './AdminOrderedMultiSelect';

const { Option } = Select;

export interface SelectOptionProps {
  id: string | number | boolean;
  label?: ReactNode;
  name: string;
  displayName?: ReactNode;
  disabled?: boolean;
  className?: string;
}

export type IdType<OptionType extends SelectOptionProps, IsToString extends boolean> = IsToString extends true ? string : OptionType['id'];

export interface BaseAdminProps<OptionType extends SelectOptionProps, IsToString extends boolean> {
  data?: OptionType[];
  loading?: boolean;
  isToString?: IsToString;
  renderOptions?: (options: OptionType[]) => JSX.Element[];
}

export type AdminSingleProps<OptionType extends SelectOptionProps = SelectOptionProps, IsToString extends boolean = boolean> = BaseAdminProps<OptionType, IsToString> &
  Omit<SelectProps<IdType<OptionType, IsToString>, OptionType>, 'value' | 'onChange'> & {
    value?: IdType<OptionType, IsToString>;
    onChange?: (value: IdType<OptionType, IsToString>) => void;
  };

export type AdminMultiProps<OptionType extends SelectOptionProps = SelectOptionProps, IsToString extends boolean = boolean> = BaseAdminProps<OptionType, IsToString> &
  Omit<SelectProps<IdType<OptionType, IsToString>[], OptionType>, 'value' | 'onChange'> & {
    value?: IdType<OptionType, IsToString>[];
    onChange?: (value: IdType<OptionType, IsToString>[]) => void;
    searchType?: 'startsWith' | 'includes';
    isSelectAll?: boolean;
  };

const AdminSelect = <OptionType extends SelectOptionProps, IsToString extends boolean = true>(
  props: (AdminMultiProps<OptionType, IsToString> | AdminSingleProps<OptionType, IsToString>) & { isMultiple?: boolean; isDraggable?: boolean },
) => {
  const { t } = useTranslation();
  const {
    isMultiple, loading = false, isToString = true as IsToString, ...rest
  } = props;

  const commonProps = useMemo(
    () => ({
      placeholder: t('all'),
      size: 'middle' as const,
      loading,
      virtual: true,
      allowClear: true,
      disabled: loading,
      suffixIcon: !loading ? (
        <StyledIconDefault>
          <IconSvg icon="DropdownIcon" width="0.7rem" />
        </StyledIconDefault>
      ) : undefined,
      renderOptions: (options: OptionType[]) => options?.map((el) => (
        <Option key={el.id.toString()} value={isToString ? el.id.toString() : el.id} label={el?.label} disabled={el.disabled} className={el.className ?? ''}>
          {el.displayName || el.name}
        </Option>
      )),
    }),
    [t, loading, isToString],
  );

  const renderComponent = () => {
    if (isMultiple) {
      const multiProps = {
        ...commonProps,
        ...(rest as AdminMultiProps<OptionType, IsToString>),
        isToString,
      };
      return props.isDraggable ? <AdminOrderedMultiSelect {...multiProps} /> : <AdminMultiSelect {...multiProps} />;
    }

    return <AdminSingleSelect {...commonProps} {...(rest as AdminSingleProps<OptionType, IsToString>)} />;
  };

  return renderComponent();
};

export default AdminSelect;
